import { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { Link } from 'react-router-dom'
import BotBadge from '../../../components/base/BotBadge'

type Activity = {
  id: string
  user_id: string
  activity_type: string
  title: string
  description?: string
  data?: any
  created_at: string
  user_profile?: {
    username: string
    avatar_url: string | null
  }
  is_bot?: boolean
}

// Mock data for when database is empty or loading fails
const mockActivities: Activity[] = [
  {
    id: '1',
    user_id: 'u1',
    activity_type: 'post_created',
    title: 'Created a new post',
    description: 'Best K-Drama OSTs of 2024',
    created_at: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    user_profile: { username: 'DramaLover88', avatar_url: null },
    is_bot: false
  },
  {
    id: 'bot1',
    user_id: 'bot1',
    activity_type: 'bot_recommendation',
    title: 'Shared a recommendation',
    description: 'Top 5 Must-Watch Historical Dramas This Week',
    created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    user_profile: { username: 'AI Assistant', avatar_url: null },
    is_bot: true
  },
  {
    id: '2',
    user_id: 'u2',
    activity_type: 'comment_created',
    title: 'Commented on a discussion',
    description: 'Great analysis! I totally agree...',
    created_at: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    user_profile: { username: 'KoreanWave', avatar_url: null },
    is_bot: false
  },
  {
    id: 'bot2',
    user_id: 'bot2',
    activity_type: 'bot_post',
    title: 'Posted a weekly roundup',
    description: 'Weekly Drama Highlights: What\'s Trending Now',
    created_at: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    user_profile: { username: 'DramaBot', avatar_url: null },
    is_bot: true
  },
  {
    id: '3',
    user_id: 'u3',
    activity_type: 'like',
    title: 'Liked a post',
    description: 'Top 10 Romance Dramas',
    created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    user_profile: { username: 'SeoulSister', avatar_url: null },
    is_bot: false
  },
  {
    id: 'bot3',
    user_id: 'bot3',
    activity_type: 'bot_comment',
    title: 'Commented with insights',
    description: 'Based on viewing patterns, you might enjoy...',
    created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    user_profile: { username: 'Recommendation Engine', avatar_url: null },
    is_bot: true
  },
  {
    id: '4',
    user_id: 'u4',
    activity_type: 'follow',
    title: 'Started following',
    description: 'DramaQueen2024',
    created_at: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    user_profile: { username: 'HallyuFan', avatar_url: null },
    is_bot: false
  },
  {
    id: '5',
    user_id: 'u5',
    activity_type: 'vip_membership',
    title: 'Became a VIP Member',
    description: 'Upgraded to Premium',
    created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    user_profile: { username: 'KDramaAddict', avatar_url: null },
    is_bot: false
  },
  {
    id: 'bot4',
    user_id: 'bot4',
    activity_type: 'bot_recommendation',
    title: 'Shared trending content',
    description: 'New Episodes Alert: Your Watchlist Updates',
    created_at: new Date(Date.now() - 50 * 60 * 1000).toISOString(),
    user_profile: { username: 'Content Curator', avatar_url: null },
    is_bot: true
  },
  {
    id: '6',
    user_id: 'u6',
    activity_type: 'group_joined',
    title: 'Joined a group',
    description: 'Historical Drama Enthusiasts',
    created_at: new Date(Date.now() - 1.2 * 60 * 60 * 1000).toISOString(),
    user_profile: { username: 'SageuKing', avatar_url: null },
    is_bot: false
  },
  {
    id: '7',
    user_id: 'u7',
    activity_type: 'review_created',
    title: 'Wrote a review',
    description: 'Queen of Tears - 9.5/10',
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    user_profile: { username: 'CriticCorner', avatar_url: null },
    is_bot: false
  },
  {
    id: '8',
    user_id: 'u8',
    activity_type: 'achievement',
    title: 'Earned a badge',
    description: 'Top Contributor',
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    user_profile: { username: 'ActiveMember', avatar_url: null },
    is_bot: false
  }
]

const CommunityActivityFeed = () => {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchActivities()
  }, [])

  const fetchActivities = async () => {
    try {
      setLoading(true)
      
      // Fetch regular user activities
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('user_activities')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(15)

      // Fetch bot profiles to identify bot users
      const { data: botProfiles, error: botError } = await supabase
        .from('bot_profiles')
        .select('id, username, avatar_url, is_active')
        .eq('is_active', true)

      if ((activitiesError && botError) || (!activitiesData && !botProfiles) || (activitiesData?.length === 0 && botProfiles?.length === 0)) {
        setActivities(mockActivities)
        setLoading(false)
        return
      }

      // Create a map of bot user IDs
      const botUserIds = new Set(botProfiles?.map(bot => bot.id) || [])
      const botProfilesMap = new Map()
      botProfiles?.forEach(bot => botProfilesMap.set(bot.id, bot))

      // Get all user IDs (both regular and bot)
      const allUserIds = [...new Set(activitiesData?.map(a => a.user_id) || [])]
      
      // Fetch regular user profiles
      const { data: userProfiles } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .in('id', allUserIds.filter(id => !botUserIds.has(id)))

      // Combine profiles
      const profilesMap = new Map()
      userProfiles?.forEach(p => profilesMap.set(p.id, p))
      botProfiles?.forEach(p => profilesMap.set(p.id, p))

      // Process activities and mark bot activities
      const processedActivities = (activitiesData || []).map(activity => ({
        ...activity,
        is_bot: botUserIds.has(activity.user_id),
        user_profile: profilesMap.get(activity.user_id) || {
          username: botUserIds.has(activity.user_id) ? 'Bot User' : 'Community Member',
          avatar_url: null
        }
      }))

      setActivities(processedActivities.length > 0 ? processedActivities : mockActivities)
    } catch (error) {
      console.error('Error fetching activities:', error)
      setActivities(mockActivities)
    } finally {
      setLoading(false)
    }
  }

  const getActivityConfig = (type: string, isBot: boolean = false) => {
    const configs: Record<string, { icon: string; color: string; bgColor: string }> = {
      // Bot-specific activity types
      bot_post: { icon: 'ri-robot-2-line', color: 'text-teal-600', bgColor: 'bg-teal-100' },
      bot_comment: { icon: 'ri-chat-ai-line', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
      bot_recommendation: { icon: 'ri-lightbulb-flash-line', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
      
      // Regular activity types
      post_created: { icon: 'ri-article-line', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
      comment_created: { icon: 'ri-chat-3-line', color: 'text-amber-600', bgColor: 'bg-amber-100' },
      like: { icon: 'ri-heart-fill', color: 'text-rose-500', bgColor: 'bg-rose-100' },
      follow: { icon: 'ri-user-follow-line', color: 'text-sky-600', bgColor: 'bg-sky-100' },
      vip_membership: { icon: 'ri-vip-crown-fill', color: 'text-yellow-500', bgColor: 'bg-yellow-100' },
      group_joined: { icon: 'ri-group-line', color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
      group_created: { icon: 'ri-team-line', color: 'text-violet-600', bgColor: 'bg-violet-100' },
      user_joined: { icon: 'ri-user-add-line', color: 'text-teal-600', bgColor: 'bg-teal-100' },
      profile_created: { icon: 'ri-user-settings-line', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
      review_created: { icon: 'ri-star-line', color: 'text-orange-500', bgColor: 'bg-orange-100' },
      achievement: { icon: 'ri-medal-line', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
      share: { icon: 'ri-share-forward-line', color: 'text-pink-600', bgColor: 'bg-pink-100' }
    }
    
    // If it's a bot activity but using regular activity type, add bot styling
    if (isBot && !type.startsWith('bot_')) {
      const config = configs[type] || { icon: 'ri-notification-line', color: 'text-gray-500', bgColor: 'bg-gray-100' }
      return {
        ...config,
        color: 'text-teal-600',
        bgColor: 'bg-teal-100'
      }
    }
    
    return configs[type] || { icon: 'ri-notification-line', color: 'text-gray-500', bgColor: 'bg-gray-100' }
  }

  const getActivityVerb = (type: string) => {
    const verbs: Record<string, string> = {
      // Bot-specific verbs
      bot_post: 'posted',
      bot_comment: 'commented',
      bot_recommendation: 'recommended',
      
      // Regular verbs
      post_created: 'created a post',
      comment_created: 'commented',
      like: 'liked',
      follow: 'followed',
      vip_membership: 'became VIP',
      group_joined: 'joined group',
      group_created: 'created group',
      user_joined: 'joined',
      profile_created: 'updated profile',
      review_created: 'wrote a review',
      achievement: 'earned badge',
      share: 'shared'
    }
    return verbs[type] || 'activity'
  }

  const formatTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`
    return date.toLocaleDateString()
  }

  const getInitials = (username: string) => {
    return username.slice(0, 2).toUpperCase()
  }

  const getAvatarColor = (username: string, isBot: boolean = false) => {
    if (isBot) {
      return 'bg-gradient-to-br from-teal-400 to-cyan-500'
    }
    
    const colors = [
      'bg-gradient-to-br from-rose-400 to-rose-600',
      'bg-gradient-to-br from-amber-400 to-amber-600',
      'bg-gradient-to-br from-emerald-400 to-emerald-600',
      'bg-gradient-to-br from-sky-400 to-sky-600',
      'bg-gradient-to-br from-violet-400 to-violet-600',
      'bg-gradient-to-br from-pink-400 to-pink-600',
      'bg-gradient-to-br from-teal-400 to-teal-600',
      'bg-gradient-to-br from-indigo-400 to-indigo-600'
    ]
    const index = username.charCodeAt(0) % colors.length
    return colors[index]
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center text-gray-900">
              <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-rose-500 to-orange-500 rounded-lg mr-3">
                <i className="ri-pulse-line text-white text-sm"></i>
              </span>
              Live Activity
            </h3>
            <span className="flex items-center text-xs text-gray-500">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1.5 animate-pulse"></span>
              Live
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse flex items-center space-x-3 p-2">
                <div className="w-9 h-9 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="flex-1">
                  <div className="h-3.5 bg-gray-200 rounded w-3/4 mb-1.5"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center text-gray-900">
            <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-rose-500 to-orange-500 rounded-lg mr-3">
              <i className="ri-pulse-line text-white text-sm"></i>
            </span>
            Live Activity
          </h3>
          <span className="flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1.5 animate-pulse"></span>
            Live
          </span>
        </div>
      </div>

      {/* Activity List */}
      <div className="divide-y divide-gray-50">
        {activities.slice(0, 8).map((activity, index) => {
          const config = getActivityConfig(activity.activity_type, activity.is_bot)
          const isVIP = activity.activity_type === 'vip_membership'
          const isBot = activity.is_bot || false
          
          return (
            <div 
              key={activity.id} 
              className={`p-3.5 hover:bg-gray-50/50 transition-all duration-200 cursor-pointer group ${
                isVIP ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : ''
              } ${isBot ? 'bg-gradient-to-r from-teal-50/30 to-transparent' : ''}`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start space-x-3">
                {/* User Avatar */}
                <div className="relative flex-shrink-0">
                  {activity.user_profile?.avatar_url ? (
                    <div className="relative">
                      <img 
                        src={activity.user_profile.avatar_url} 
                        alt={activity.user_profile.username}
                        className={`w-9 h-9 rounded-full object-cover shadow-sm ${
                          isBot ? 'ring-2 ring-teal-400/50' : 'ring-2 ring-white'
                        }`}
                      />
                      {isBot && (
                        <div className="absolute -top-0.5 -right-0.5">
                          <BotBadge size="xs" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm ${getAvatarColor(activity.user_profile?.username || 'U', isBot)}`}>
                        {isBot ? (
                          <i className="ri-robot-2-fill text-sm"></i>
                        ) : (
                          getInitials(activity.user_profile?.username || 'U')
                        )}
                      </div>
                      {isBot && (
                        <div className="absolute -top-0.5 -right-0.5">
                          <BotBadge size="xs" />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Activity Type Badge */}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${config.bgColor} flex items-center justify-center ring-2 ring-white`}>
                    <i className={`${config.icon} ${config.color} text-[10px]`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm leading-snug">
                      <span className={`font-semibold group-hover:text-rose-600 transition-colors inline-flex items-center gap-1 ${
                        isBot ? 'text-teal-700' : 'text-gray-900'
                      }`}>
                        {activity.user_profile?.username}
                        {isBot && <BotBadge size="xs" />}
                      </span>
                      <span className="text-gray-500 ml-1">
                        {getActivityVerb(activity.activity_type)}
                      </span>
                    </p>
                    <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2">
                      {formatTimeAgo(activity.created_at)}
                    </span>
                  </div>
                  
                  {activity.description && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {isVIP ? (
                        <span className="inline-flex items-center">
                          <i className="ri-vip-crown-fill text-yellow-500 mr-1"></i>
                          {activity.description}
                        </span>
                      ) : (
                        activity.description
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-100">
        {/* Removed "View All Activity" link */}
      </div>
    </div>
  )
}

export default CommunityActivityFeed
